import {
  useCallback,
  useId,
  useRef,
  useState,
  type ChangeEventHandler,
  type FocusEventHandler,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { HelpIcon } from '../HelpIcon/HelpIcon';
import { Tag } from '../Tag/Tag';
import styles from './TextareaField.module.css';

/**
 * Figma: `Textarea input field` (825:20261) — a labelled multi-line field, 140px
 * tall on 12px padding, with a help icon beside the label, a hint line under it,
 * a tag-entry mode and a resize grip in the bottom-right corner. 14 variants
 * across three variant properties and five toggles; the set carries no
 * annotations.
 *
 * Same mapping as `InputField`: only `Type` becomes an enum prop, and every
 * `State` is real — `Placeholder`/`Default` is whether there is a value,
 * `Focused` is focus, `Disabled` is the prop. `Destructive=True` is
 * `errorMessage` being present.
 *
 * One thing differs from `InputField`: the label row carries a `Help icon`
 * instance, which its `Input field` sibling does not — that field has a
 * `Help icon` boolean property but no node behind it (plan.md §7).
 *
 * The resting ring used to differ too, binding `border/primary` where the
 * single-line field bound `component/input/border`. The designer rebound it on
 * 2026-09-14 and both now use `--input-border`.
 */
export type TextareaFieldType = 'default' | 'tags';

/** Force a state for documentation and visual regression only. */
export type TextareaFieldState = 'default' | 'focused';

export type TextareaFieldProps = {
  /** Figma `Type`. `tags` renders `value` as `Tag`s above the caret. */
  type?: TextareaFieldType;
  /** Figma's `Label` toggle. */
  label?: string;
  /** Figma's `↳ Required *` toggle. */
  required?: boolean;
  /**
   * Figma's `↳ Help icon` toggle. The string is the tooltip's text: the instance
   * is `Help` `Tooltip=Top no arrow` (99:4840), so the icon reveals a bubble
   * rather than only carrying a label.
   */
  helpText?: string;
  /** Figma's `Supporting text` on that tooltip — a second, dimmer line. */
  helpSupportingText?: string;
  /** Figma's `Hint text` toggle. */
  hint?: string;
  /** Figma `Destructive=True`. Replaces `hint` and turns the ring red. */
  errorMessage?: string;
  /**
   * Figma's `Resize handle` toggle. The grip is drawn in the corner; dragging it
   * changes the field's height. Figma draws the glyph but defines no behaviour —
   * see the summary.
   */
  resizable?: boolean;
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  /** `type="tags"` only. Controlled alongside `onTagsChange`. */
  tags?: string[];
  defaultTags?: string[];
  onTagsChange?: (tags: string[]) => void;
  /** Figma pins the field at 140px. */
  rows?: number;
  name?: string;
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
  onFocus?: FocusEventHandler<HTMLTextAreaElement>;
  onBlur?: FocusEventHandler<HTMLTextAreaElement>;
  /** Accessible name when there is no visible `label`. Not in Figma. */
  'aria-label'?: string;
  state?: TextareaFieldState;
  className?: string;
};

/** Figma pins `Input` at 140px tall. Dragging the grip never goes below it. */
const MIN_HEIGHT = 140;

export function TextareaField({
  type = 'default',
  label,
  required = false,
  helpText,
  helpSupportingText,
  hint,
  errorMessage,
  resizable = true,
  placeholder,
  value,
  defaultValue,
  onValueChange,
  disabled = false,
  tags,
  defaultTags,
  onTagsChange,
  name,
  onChange,
  onFocus,
  onBlur,
  'aria-label': ariaLabel,
  state,
  className,
}: TextareaFieldProps) {
  const fieldId = useId();
  const messageId = useId();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputBoxRef = useRef<HTMLDivElement>(null);

  const [ownValue, setOwnValue] = useState(defaultValue ?? '');
  const currentValue = value ?? ownValue;

  const [ownTags, setOwnTags] = useState<string[]>(defaultTags ?? []);
  const currentTags = tags ?? ownTags;

  /** Set by the grip; `undefined` until the field is first dragged. */
  const [draggedHeight, setDraggedHeight] = useState<number>();

  const destructive = errorMessage !== undefined;
  const showTags = type === 'tags' && currentTags.length > 0;

  const commitValue = (next: string) => {
    if (value === undefined) setOwnValue(next);
    onValueChange?.(next);
  };

  const commitTags = (next: string[]) => {
    if (tags === undefined) setOwnTags(next);
    onTagsChange?.(next);
  };

  /*
   * Same convention as `InputField`: Enter commits, Backspace on empty removes.
   *
   * **Tab commits too, but only when there is something to commit** (added
   * 2026-09-20). Custom Reports' `Configure campaigns` draws this field with the
   * placeholder "Separate keywords by pressing ‘tab’…", so the design names Tab
   * as the separator. Taking Tab unconditionally would trap a keyboard user in
   * the field — Tab is how they leave it. So a Tab with text in the caret
   * commits that text and stays put; a Tab on an empty caret does what Tab
   * always does and moves focus on. Shift+Tab is never taken.
   */
  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (type !== 'tags' || disabled) return;

    const commits = event.key === 'Enter' || (event.key === 'Tab' && !event.shiftKey);
    if (commits && currentValue.trim() !== '') {
      event.preventDefault();
      commitTags([...currentTags, currentValue.trim()]);
      commitValue('');
      return;
    }

    if (event.key === 'Backspace' && currentValue === '' && currentTags.length > 0) {
      commitTags(currentTags.slice(0, -1));
    }
  };

  /*
   * Not in Figma — Figma draws the grip as artwork and says nothing about what
   * it does. The native `resize` property would be the cheap answer, but the
   * browser paints its own grabber in the same corner and the two would sit on
   * top of each other, so `resize` is off and this drives the height directly.
   * Pointer events cover mouse, touch and pen in one path.
   */
  const handleGripDown = useCallback(
    (event: ReactPointerEvent<HTMLSpanElement>) => {
      if (disabled) return;
      const box = inputBoxRef.current;
      if (!box) return;

      event.preventDefault();
      const startY = event.clientY;
      const startHeight = box.getBoundingClientRect().height;
      const grip = event.currentTarget;
      grip.setPointerCapture(event.pointerId);

      const move = (moveEvent: PointerEvent) => {
        setDraggedHeight(Math.max(MIN_HEIGHT, startHeight + moveEvent.clientY - startY));
      };
      const up = () => {
        grip.removeEventListener('pointermove', move);
        grip.removeEventListener('pointerup', up);
        grip.removeEventListener('pointercancel', up);
      };

      grip.addEventListener('pointermove', move);
      grip.addEventListener('pointerup', up);
      grip.addEventListener('pointercancel', up);
    },
    [disabled],
  );

  const removeTag = (index: number) => {
    commitTags(currentTags.filter((_, i) => i !== index));
    textareaRef.current?.focus();
  };

  const inputClasses = [
    styles.input,
    disabled ? styles.disabled : null,
    destructive ? styles.destructive : null,
    !disabled && state === 'focused' ? styles.focused : null,
    !disabled && state === undefined ? styles.interactive : null,
  ]
    .filter(Boolean)
    .join(' ');

  const control = (
    <textarea
      ref={textareaRef}
      id={fieldId}
      className={styles.control}
      name={name}
      placeholder={placeholder}
      value={currentValue}
      disabled={disabled}
      /*
       * **Never the DOM `required` at `type="tags"`**, and `aria-required`
       * instead.
       *
       * At `type="default"` the textarea holds the field's value and the
       * constraint is exactly right. At `type="tags"` it does not: the value is
       * the tag list, and this element is only the caret you type the next tag
       * into — it is empty at rest and empty again the moment a tag commits. A
       * `required` on it is a constraint nothing can ever satisfy, so the
       * browser blocks `submit` on the whole form, silently and forever.
       *
       * Found by measurement on 2026-09-21: Quick search's `Search` button did
       * nothing, and `form.checkValidity()` reported this textarea as the one
       * invalid element with "Please fill out this field." while two tags sat
       * beside it.
       *
       * `aria-required` keeps the promise the asterisk makes without handing
       * the browser a constraint it cannot check. A caller that needs the
       * emptiness *enforced* does it where the tags live — Quick search
       * disables `Search` until there is at least one.
       */
      required={type === 'tags' ? undefined : required}
      aria-required={type === 'tags' && required ? true : undefined}
      aria-label={label === undefined ? ariaLabel : undefined}
      aria-invalid={destructive || undefined}
      aria-describedby={hint !== undefined || destructive ? messageId : undefined}
      onChange={(event) => {
        commitValue(event.target.value);
        onChange?.(event);
      }}
      onKeyDown={handleKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );

  return (
    <div className={[styles.root, className].filter(Boolean).join(' ')}>
      <div className={styles.inputWithLabel}>
        {label !== undefined && (
          <div className={styles.labelWrapper}>
            <label className={styles.label} htmlFor={fieldId}>
              {label}
            </label>
            {required && (
              <span className={styles.asterisk} aria-hidden="true">
                *
              </span>
            )}
            {helpText !== undefined && (
              <HelpIcon
                text={helpText}
                supportingText={helpSupportingText}
                placement="topNoArrow"
                label={`About ${label}`}
              />
            )}
          </div>
        )}

        <div
          ref={inputBoxRef}
          className={inputClasses}
          style={draggedHeight === undefined ? undefined : { height: draggedHeight }}
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) textareaRef.current?.focus();
          }}
        >
          {/*
           * Figma nests the caret directly in `Input` on `Type=Default` and wraps
           * it in a `Content` frame beside the tags on `Type=Tags`, where the
           * field's own layout also turns vertical.
           *
           * **On `Type=Tags` the wrapper is always there, and only the tag row
           * comes and goes** (fixed 2026-09-20). It used to appear with the first
           * tag, which moved the `<textarea>` to a new parent — React unmounts
           * and remounts an element that changes parent, so committing the first
           * keyword dropped focus to `<body>` and the second one landed nowhere.
           * Measured with Enter and with Tab alike. Keeping the caret in one slot
           * of one wrapper is what keeps it mounted. With no tags the wrapper
           * holds only the caret and fills the field, so the empty field lays
           * out as before — checked against the stories' geometry.
           */}
          {type === 'tags' ? (
            <div
              className={[styles.content, showTags ? styles.contentWithTags : null]
                .filter(Boolean)
                .join(' ')}
              /*
               * The same click-to-focus the input box above carries, and for a
               * reason that only appeared once the caret stopped filling the
               * field: with enough tags to fill the row the caret wraps to its
               * own line (see `.contentWithTags .control`), so the empty space
               * under it belongs to this wrapper rather than to the textarea.
               * Without this, clicking the lower two thirds of a filled tag
               * field does nothing — the box's own handler only fires for its
               * own padding, and this element is in the way.
               */
              onPointerDown={(event) => {
                if (event.target === event.currentTarget) textareaRef.current?.focus();
              }}
            >
              {showTags && (
                <span className={styles.tags}>
                  {currentTags.map((tag, index) => (
                    <Tag
                      key={tag}
                      onRemove={disabled ? undefined : () => removeTag(index)}
                      removeLabel={`Remove ${tag}`}
                    >
                      {tag}
                    </Tag>
                  ))}
                </span>
              )}
              {control}
            </div>
          ) : (
            control
          )}

          {/*
           * Figma `Resize handle` — an absolutely-positioned 12x12 frame pinned
           * 6px off the bottom-right corner, two rounded 1px strokes in
           * `border/primary`. Drawn inline rather than through `Icon`, which
           * only renders filled paths.
           */}
          {resizable && (
            <span
              className={styles.resizeHandle}
              onPointerDown={handleGripDown}
              aria-hidden="true"
            >
              <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 2L2 10" stroke="currentColor" strokeLinecap="round" />
                <path d="M11 7L7 11" stroke="currentColor" strokeLinecap="round" />
              </svg>
            </span>
          )}
        </div>
      </div>

      {destructive ? (
        <p className={styles.errorMessage} id={messageId}>
          {errorMessage}
        </p>
      ) : (
        hint !== undefined && (
          <p className={styles.hint} id={messageId}>
            {hint}
          </p>
        )
      )}
    </div>
  );
}
